import { useEffect, useRef, useState } from "react";
import { PenTool, Trash2, Type as TypeIcon } from "lucide-react";

/**
 * Compact signature pad with two modes:
 *   - "draw"    : freehand canvas
 *   - "stampatello" (typed): user types their name in a signature-like font
 * Exposes `onChange(dataURL | "")` — a PNG data URL when signed, empty string
 * when cleared.
 */
export default function SignaturePad({ value, onChange, label, testId = "signature-pad" }) {
  const [mode, setMode] = useState("draw");
  const [typed, setTyped] = useState("");
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    if (!canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    ctx.strokeStyle = "#0f172a";
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, [mode]);

  useEffect(() => {
    // Render typed signature to canvas so it becomes a PNG on submit.
    if (mode !== "stampatello" || !canvasRef.current) return;
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, c.width, c.height);
    if (typed) {
      ctx.fillStyle = "#0f172a";
      ctx.font = "italic 34px 'Dancing Script', 'Segoe Script', cursive";
      ctx.textBaseline = "middle";
      ctx.fillText(typed, 14, c.height / 2);
      onChange(c.toDataURL("image/png"));
    } else {
      onChange("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typed, mode]);

  const point = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: (t?.clientX ?? e.clientX) - rect.left,
      y: (t?.clientY ?? e.clientY) - rect.top,
    };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const {x,y}=point(e); const ctx=canvasRef.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(x,y); };
  const move  = (e) => { if(!drawing.current) return; e.preventDefault(); const {x,y}=point(e); const ctx=canvasRef.current.getContext("2d"); ctx.lineTo(x,y); ctx.stroke(); };
  const end   = () => { if(!drawing.current) return; drawing.current=false; onChange(canvasRef.current.toDataURL("image/png")); };

  const clear = () => {
    setTyped("");
    if (canvasRef.current) {
      const c = canvasRef.current;
      const ctx = c.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, c.width, c.height);
    }
    onChange("");
  };

  return (
    <div data-testid={testId}>
      {label && <p className="text-sm font-medium text-slate-800 mb-1.5">{label}</p>}
      <div className="flex gap-1.5 mb-2">
        <button
          type="button"
          onClick={() => { setMode("draw"); clear(); }}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${mode === "draw" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}
          data-testid={`${testId}-mode-draw`}
        >
          <PenTool size={12} /> Firma a mano
        </button>
        <button
          type="button"
          onClick={() => { setMode("stampatello"); clear(); }}
          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${mode === "stampatello" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"}`}
          data-testid={`${testId}-mode-stampatello`}
        >
          <TypeIcon size={12} /> Firma in stampatello
        </button>
        <button
          type="button"
          onClick={clear}
          className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-500 border border-slate-300 bg-white hover:text-red-700"
          data-testid={`${testId}-clear`}
        >
          <Trash2 size={12} /> Pulisci
        </button>
      </div>
      {mode === "stampatello" && (
        <input
          type="text"
          className="w-full mb-2 h-9 px-2 border border-slate-300 rounded text-sm"
          placeholder="Es. Mario Rossi"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          data-testid={`${testId}-typed-input`}
        />
      )}
      <canvas
        ref={canvasRef}
        width={520}
        height={120}
        className="w-full h-32 border border-slate-300 rounded bg-white touch-none"
        onMouseDown={mode === "draw" ? start : undefined}
        onMouseMove={mode === "draw" ? move : undefined}
        onMouseUp={mode === "draw" ? end : undefined}
        onMouseLeave={mode === "draw" ? end : undefined}
        onTouchStart={mode === "draw" ? start : undefined}
        onTouchMove={mode === "draw" ? move : undefined}
        onTouchEnd={mode === "draw" ? end : undefined}
        data-testid={`${testId}-canvas`}
      />
      {value && (
        <p className="text-[11px] text-emerald-700 mt-1" data-testid={`${testId}-signed-badge`}>
          ✓ Firma acquisita
        </p>
      )}
    </div>
  );
}
